import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const About = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Our Story — BrightWings</title>
        <meta name="description" content="BrightWings exists because too many founders are building blind. Not for lack of vision — for lack of direction. Here's our story." />
        <link rel="canonical" href="https://brightwings.io/about" />
        <meta property="og:url" content="https://brightwings.io/about" />
        <meta property="og:title" content="Our Story — BrightWings" />
        <meta property="og:description" content="BrightWings exists because too many founders are building blind. Not for lack of vision — for lack of direction. Here's our story." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Story — BrightWings" />
        <meta name="twitter:description" content="BrightWings exists because too many founders are building blind. Not for lack of vision — for lack of direction. Here's our story." />
      </Helmet>
      <Navigation />

      {/* Hero — The Character and their Problem */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Our Story</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              You have a vision.{' '}
              <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>Building it shouldn't feel like this.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto' }}>
              Most founders start with excitement. Then the questions pile up, the wrong hires happen, and somewhere between the idea and the launch, the vision gets lost.
            </p>
          </div>
        </div>
      </section>

      {/* The Struggle — External, Internal, Philosophical */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '12px' }}>
              The problem isn't your idea.
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', maxWidth: '540px', margin: '0 auto', lineHeight: 1.7 }}>
              It's that no one prepared you for what comes after the idea.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                label: "What's happening",
                heading: 'The path forward is unclear.',
                body: 'The right tools seem elusive. Developer conversations are confusing. Every decision feels like a potential mistake that could cost months.',
              },
              {
                label: 'How it feels',
                heading: 'You feel unprepared and isolated.',
                body: "Handing your vision to someone else is frightening when you can't fully describe what you need. The excitement fades. Frustration creeps in.",
              },
              {
                label: "What's at stake",
                heading: 'Your original vision is at risk.',
                body: "Founders who build without direction don't just waste money — they build the wrong thing. Then they start over.",
              },
            ].map((item, i) => (
              <div key={i} className="bw-card">
                <div className="bw-card-body">
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--bw-blue)', fontFamily: 'var(--font-display)' }}>
                      {item.label}
                    </span>
                  </div>
                  <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '12px' }}>{item.heading}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Guide — Empathy + Authority */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <div className="bw-eyebrow-pill" style={{ marginBottom: '20px' }}>Where BrightWings comes in</div>
              <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 42px)', marginBottom: '20px' }}>
                You don't need more tools. You need a guide who's been here before.
              </h2>
              <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)', marginBottom: '20px' }}>
                We've worked with founders from every background — solo creators, first-time entrepreneurs, operators pivoting into tech. The struggle is always the same: brilliant idea, no roadmap.
              </p>
              <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)' }}>
                BrightWings exists to be the partner you needed from the start. Not a vendor. Not a dev shop. A guide who helps you decide what to build, why to build it, and how to get it done without losing your vision in the process.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { stat: '15+', label: 'Years of product and engineering experience' },
                { stat: '50+', label: 'Founders helped' },
                { stat: '100%', label: 'Code and IP ownership transferred to you' },
              ].map((item, i) => (
                <div key={i} className="bw-card" style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '36px', color: 'var(--bw-blue)', lineHeight: 1, minWidth: '80px' }}>
                      {item.stat}
                    </div>
                    <div style={{ color: 'var(--bw-text-mid)', fontSize: '15px', lineHeight: 1.5 }}>{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Plan — What working together looks like */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '12px' }}>
              A clear path forward.
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', maxWidth: '500px', margin: '0 auto', lineHeight: 1.7 }}>
              Working with BrightWings isn't complicated. Here's exactly what it looks like.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {[
              {
                step: '01',
                heading: 'Start a conversation.',
                body: 'Tell us what\'s broken, slow, or unclear in your business. No technical knowledge required. We speak founder first.',
              },
              {
                step: '02',
                heading: 'Get clarity on what to build.',
                body: 'We help you figure out if software is the right solution, what to build first, and what tradeoffs you\'re accepting. You get a clear scope — not a vague estimate.',
              },
              {
                step: '03',
                heading: 'Choose how to move forward.',
                body: 'Take the blueprint to your own team. Add ongoing advisory. Or hire us to build. You own the decision. We support whichever path makes sense.',
              },
            ].map((item, i, arr) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '32px', padding: '32px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--bw-border)' : 'none', alignItems: 'start' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '48px', color: 'var(--bw-blue)', opacity: 0.25, lineHeight: 1 }}>
                  {item.step}
                </div>
                <div>
                  <h3 style={{ ...hStyle, fontSize: '22px', marginBottom: '10px' }}>{item.heading}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', fontSize: '16px', lineHeight: 1.7, maxWidth: '600px' }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Stakes */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <blockquote style={{ borderLeft: '3px solid var(--bw-blue)', paddingLeft: '28px', margin: 0 }}>
            <p style={{ fontSize: 'clamp(20px, 2.5vw, 28px)', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--bw-dark)', lineHeight: 1.4, marginBottom: '16px' }}>
              "The founders we work with don't look back and wish they'd moved faster. They look back and wish they'd had a guide sooner."
            </p>
            <p style={{ fontSize: '15px', color: 'var(--bw-text-mid)', lineHeight: 1.7 }}>
              The cost of building without direction isn't just wasted money. It's the excitement that drains out of the process — the slow realization that what you built isn't quite what you meant. That's what we exist to prevent.
            </p>
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>
            Don't build blind.
          </h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Start a conversation. Tell us what you're trying to build. We'll help you figure out what to do next.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            Start a conversation <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
